import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, Image, Upload, Search, Bold, Italic, Link2, ListOrdered, List } from 'lucide-react';
import MarkdownPreview from './MarkdownPreview';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider'

interface TextSelection {
    start: number;
    end: number;
}

interface ListFormatAction {
    selectionStart: number;
    selectionEnd: number;
    text: string;
    type: 'unordered' | 'ordered';
}

interface QuestionComposerProps {
    onSubmit: () => void;
}

const QuestionComposer: React.FC<QuestionComposerProps> = ({ onSubmit }) => {

    const auth = useAuth();

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        tags: [] as string[],
        images: [] as string[]
    });


    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [selection, setSelection] = useState<TextSelection>({ start: 0, end: 0 });
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');

    const [currentTag, setCurrentTag] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [similarQuestions, setSimilarQuestions] = useState<Array<{ id: number, title: string, votes: number }>>([]);
    const [isUploading, setIsUploading] = useState(false);


    // Save selection when textarea is focused
    const handleTextareaSelect = () => {
        if (textareaRef.current) {
            setSelection({
                start: textareaRef.current.selectionStart,
                end: textareaRef.current.selectionEnd
            });
        }
    };

    // Format helpers
    const insertFormatting = (startTag: string, endTag: string = startTag) => {
        if (textareaRef.current) {
            const textarea = textareaRef.current;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = textarea.value.substring(start, end);

            // If there's no selection, insert tags with cursor in between
            if (start === end) {
                const newText =
                    textarea.value.substring(0, start) +
                    startTag + endTag +
                    textarea.value.substring(end);

                setFormData(prev => ({
                    ...prev,
                    content: newText
                }));

                // Place cursor between tags
                setTimeout(() => {
                    textarea.selectionStart = start + startTag.length;
                    textarea.selectionEnd = start + startTag.length;
                    textarea.focus();
                }, 0);
            } else {
                // Wrap selected text with tags
                const newText =
                    textarea.value.substring(0, start) +
                    startTag + selectedText + endTag +
                    textarea.value.substring(end);

                setFormData(prev => ({
                    ...prev,
                    content: newText
                }));

                // Maintain selection
                setTimeout(() => {
                    textarea.selectionStart = start + startTag.length;
                    textarea.selectionEnd = end + startTag.length;
                    textarea.focus();
                }, 0);
            }
        }
    };

    const handleBold = () => insertFormatting('**');
    const handleItalic = () => insertFormatting('*');

    const handleLink = () => {
        if (textareaRef.current) {
            const hasSelection = textareaRef.current.selectionStart !== textareaRef.current.selectionEnd;
            if (hasSelection) {
                setShowLinkModal(true);
            } else {
                // If no text is selected, insert a template link
                insertFormatting('[', '](url)');
            }
        }
    };

    const insertLink = () => {
        if (textareaRef.current && linkUrl) {
            const start = selection.start;
            const end = selection.end;
            const selectedText = textareaRef.current.value.substring(start, end);
            const linkMarkdown = `[${selectedText}](${linkUrl})`;

            const newText =
                textareaRef.current.value.substring(0, start) +
                linkMarkdown +
                textareaRef.current.value.substring(end);

            setFormData(prev => ({
                ...prev,
                content: newText
            }));

            setShowLinkModal(false);
            setLinkUrl('');
        }
    };

    // Function to handle list formatting
    const handleListFormat = ({ selectionStart, selectionEnd, text, type }: ListFormatAction): string => {
        const selectedText = text.substring(selectionStart, selectionEnd);
        const lines = selectedText.split('\n');

        const formattedLines = lines.map((line, index) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return '';

            if (type === 'unordered') {
                return `* ${trimmedLine}`;
            } else {
                return `${index + 1}. ${trimmedLine}`;
            }
        });

        const result = formattedLines.join('\n');

        return text.substring(0, selectionStart) + result + text.substring(selectionEnd);
    };

    // Add these button handlers to your component
    const handleUnorderedList = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const updatedContent = handleListFormat({
            selectionStart: start,
            selectionEnd: end,
            text: formData.content,
            type: 'unordered'
        });

        setFormData({
            ...formData,
            content: updatedContent
        });
    };

    const handleOrderedList = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const updatedContent = handleListFormat({
            selectionStart: start,
            selectionEnd: end,
            text: formData.content,
            type: 'ordered'
        });

        setFormData({
            ...formData,
            content: updatedContent
        });
    };

    // Simulated similar questions search
    useEffect(() => {
        if (formData.title.length > 3) {
            // Simulate API call delay
            const timer = setTimeout(() => {
                // This would be an API call in a real application
                const mockSimilarQuestions = [
                    {
                        id: 1,
                        title: `How to ${formData.title.toLowerCase()}`,
                        votes: 15,
                        answers: 3
                    },
                    {
                        id: 2,
                        title: `Best practices for ${formData.title.toLowerCase()}`,
                        votes: 25,
                        answers: 5
                    }
                ];
                setSimilarQuestions(mockSimilarQuestions);
            }, 300);

            return () => clearTimeout(timer);
        } else {
            setSimilarQuestions([]);
        }
    }, [formData.title]);

    // Popular tags suggestion - in real app, could come from API
    const suggestedTags = [
        'react', 'typescript', 'javascript', 'node.js',
        'authentication', 'api', 'state-management', 'testing'
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const form = new FormData();
        form.append('blogForm', JSON.stringify(formData));

        try {
            const response = await apiClient.post(
                '/v1/forum/new', form,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            onSubmit();
        } catch (error) {
            console.error('Request failed:', error);
        }

    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        setIsUploading(true);
        try {
            // In a real app, you would upload to your server/cloud storage
            // Here we're just creating object URLs for preview
            const newImages = Array.from(files).map(file => URL.createObjectURL(file));

            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...newImages]
            }));
        } catch (error) {
            console.error('Error uploading images:', error);
            // Handle error appropriately
        } finally {
            setIsUploading(false);
        }
    };

    const handleImageRemove = (imageUrl: string) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter(img => img !== imageUrl)
        }));
        URL.revokeObjectURL(imageUrl); // Cleanup object URL
    };

    const handleTagAdd = () => {
        if (currentTag && !formData.tags.includes(currentTag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, currentTag.toLowerCase()]
            }));
            setCurrentTag('');
        }
    };

    const handleTagRemove = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleSuggestedTagClick = (tag: string) => {
        if (!formData.tags.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
        }
    };

    return (
        <>



            <form onSubmit={handleSubmit}>
                {/* Title Input */}
                <div className="mb-4">
                    <label className="form-label fw-bold">
                        Question Title
                        <span className="text-secondary ms-2 fw-normal">
                            Be specific and imagine you're asking another person
                        </span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({
                            ...prev,
                            title: e.target.value
                        }))}
                        placeholder="e.g. How to handle authentication in React with TypeScript?"
                    />
                    {errors.title && (
                        <div className="invalid-feedback d-flex align-items-center">
                            <AlertCircle size={16} className="me-2" />
                            {errors.title}
                        </div>
                    )}
                </div>

                {/* Similar Questions */}
                {similarQuestions.length > 0 && (
                    <div className="alert alert-warning mb-4">
                        <div className="d-flex align-items-center mb-2">
                            <Search size={20} className="me-2" />
                            <strong>Similar questions found:</strong>
                        </div>
                        <ul className="list-unstyled mb-0">
                            {similarQuestions.map(question => (
                                <li key={question.id} className="mb-2">
                                    <a href={`#question-${question.id}`} className="text-decoration-none">
                                        {question.title}
                                    </a>
                                    <span className="text-secondary ms-2">
                                        ({question.votes} votes)
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Question Details */}
                <div className="mb-4">
                    <label className="form-label fw-bold">
                        Question Details
                        <span className="text-secondary ms-2 fw-normal">
                            Include all the information someone would need to answer
                        </span>
                    </label>
                    <div className="border rounded">
                        {/* Formatting Toolbar */}
                        <div className="border-bottom p-2 bg-light">
                            <div className="btn-group">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleBold}
                                    title="Bold (Ctrl+B)"
                                >
                                    <Bold size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleItalic}
                                    title="Italic (Ctrl+I)"
                                >
                                    <Italic size={16} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleLink}
                                    title="Insert Link (Ctrl+K)"
                                >
                                    <Link2 size={16} />
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                    onClick={handleUnorderedList} title="List"
                                >
                                    <List size={16} />
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                    onClick={handleOrderedList} title="List ordered"
                                >
                                    <ListOrdered size={16} />

                                </button>
                            </div>
                        </div>
                        <textarea
                            ref={textareaRef}
                            className={`form-control border-0 ${errors?.content ? 'is-invalid' : ''}`}
                            rows={10}
                            value={formData.content}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                content: e.target.value
                            }))}
                            onSelect={handleTextareaSelect}
                            placeholder="Explain your question in detail... Use markdown formatting: **bold**, *italic*, [link](url)"
                        />
                    </div>
                    {errors.content && (
                        <div className="text-danger small mt-2 d-flex align-items-center">
                            <AlertCircle size={16} className="me-2" />
                            {errors.content}
                        </div>
                    )}

                    {/* Preview Section */}
                    <MarkdownPreview content={formData.content} />

                </div>

                {/* Link Modal */}
                {showLinkModal && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Insert Link</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowLinkModal(false);
                                            setLinkUrl('');
                                        }}
                                    />
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">URL</label>
                                        <input
                                            type="url"
                                            className="form-control"
                                            value={linkUrl}
                                            onChange={(e) => setLinkUrl(e.target.value)}
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowLinkModal(false);
                                            setLinkUrl('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={insertLink}
                                    >
                                        Insert Link
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Image Upload Section */}
                <div className="mb-4">
                    <label className="form-label fw-bold">
                        Images
                        <span className="text-secondary ms-2 fw-normal">
                            Add images to help explain your question
                        </span>
                    </label>

                    {/* Image Preview Grid */}
                    {formData.images.length > 0 && (
                        <div className="row g-2 mb-3">
                            {formData.images.map((image, index) => (
                                <div key={index} className="col-md-3">
                                    <div className="position-relative">
                                        <img
                                            src={image}
                                            alt={`Upload ${index + 1}`}
                                            className="img-fluid rounded"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                            onClick={() => handleImageRemove(image)}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload Button */}
                    <div className="d-flex align-items-center">
                        <label className="btn btn-outline-secondary me-2 mb-0">
                            <input
                                type="file"
                                className="d-none"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                disabled={isUploading}
                            />
                            <Image size={16} className="me-2" />
                            Add Images
                        </label>
                        {isUploading && (
                            <div className="text-secondary">
                                <Upload size={16} className="me-2" />
                                Uploading...
                            </div>
                        )}
                    </div>
                </div>

                {/* Tags Input */}
                <div className="mb-4">
                    <label className="form-label fw-bold">
                        Tags
                        <span className="text-secondary ms-2 fw-normal">
                            Add up to 5 tags to describe what your question is about
                        </span>
                    </label>

                    {/* Current Tags */}
                    <div className="mb-2">
                        {formData.tags.map(tag => (
                            <span
                                key={tag}
                                className="badge bg-primary me-2 mb-2"
                            >
                                {tag}
                                <button
                                    type="button"
                                    className="btn btn-link text-white p-0 ms-2"
                                    onClick={() => handleTagRemove(tag)}
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        ))}
                    </div>

                    {/* Tag Input */}
                    <div className="input-group">
                        <input
                            type="text"
                            className={`form-control ${errors.tags ? 'is-invalid' : ''}`}
                            placeholder="Add a tag..."
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleTagAdd();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleTagAdd}
                        >
                            Add
                        </button>
                    </div>
                    {errors.tags && (
                        <div className="text-danger small mt-2 d-flex align-items-center">
                            <AlertCircle size={16} className="me-2" />
                            {errors.tags}
                        </div>
                    )}

                    {/* Suggested Tags */}
                    <div className="mt-3">
                        <small className="text-secondary">Suggested tags:</small>
                        <div className="mt-2">
                            {suggestedTags.map(tag => (
                                <button
                                    key={tag}
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary me-2 mb-2"
                                    onClick={() => handleSuggestedTagClick(tag)}
                                    disabled={formData.tags.includes(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Review Instructions */}
                <div className="alert alert-info" role="alert">
                    <h6 className="alert-heading">Before you post, please make sure:</h6>
                    <ul className="mb-0">
                        <li>Your question title is specific and descriptive</li>
                        <li>You've included enough details and context</li>
                        <li>You've added relevant tags</li>
                        <li>You've checked for similar questions</li>
                    </ul>
                </div>

                {/* Submit Button */}
                <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary">
                        Post Your Question
                    </button>
                    <button type="button" className="btn btn-outline-secondary">
                        Preview
                    </button>
                </div>
            </form>

        </>
    );
};

export default QuestionComposer;